import { api } from './api';

export enum UserRole {
  ADMIN = 'ADMIN',
  DIRECTOR = 'DIRECTOR',
  USER = 'USER',
}

export interface RegisterUserData {
  email: string;
  phone?: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  agencyIds: number[];
  isEnable: boolean;
  hasMarketingHubAccess: boolean;
  hasLeaderboardAccess: boolean;
  leaderboardOfficeIds?: number[];
}

export interface UserError {
  message: string[];
  error: string;
  statusCode: number;
}

export interface User {
  id: number;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isEnable: boolean;
  createdAt: string;
  agencies: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  }[];
  hasMarketingHubAccess?: boolean;
  hasLeaderboardAccess?: boolean;
  leaderboardOffices?: {
    id: number;
    name: string;
  }[];
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  agencyIds?: number[];
  role?: UserRole;
  hasMarketingHubAccess?: boolean;
  hasLeaderboardAccess?: boolean;
  leaderboardOfficeIds?: number[];
}

export interface UsersResponse {
  users: User[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface GetUsersParams {
  limit?: number;
  offset?: number;
  role?: UserRole;
  agencyIds?: string;
}

export const users = {
  getById: async (id: number): Promise<User> => {
    try {
      const response = await api.get<User>(`/users/${id}`);
      return response;
    } catch (error) {
      const userError = error as UserError;
      throw userError;
    }
  },

  create: async (userData: RegisterUserData): Promise<void> => {
    try {
      return await api.post('/users', userData);
    } catch (error) {
      const userError = error as UserError;
      throw userError;
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      return await api.delete(`/users/${id}`);
    } catch (error) {
      const userError = error as UserError;
      throw userError;
    }
  },

  deleteMany: async (userIds: number[]): Promise<void> => {
    try {
      return await api.delete('/users', { userIds });
    } catch (error) {
      const userError = error as UserError;
      throw userError;
    }
  },

  update: async (id: number, userData: UpdateUserData): Promise<void> => {
    try {
      return await api.patch(`/users/${id}`, userData);
    } catch (error) {
      const userError = error as UserError;
      throw userError;
    }
  },

  getList: async (params: GetUsersParams): Promise<UsersResponse> => {
    try {
      const response = await api.get<UsersResponse>('/users', { params });
      return response;
    } catch (error) {
      const userError = error as UserError;
      throw userError;
    }
  },

  getAll: async (): Promise<User[]> => {
    try {
      const response = await api.get<User[]>('/users');
      return response;
    } catch (error) {
      const apiError = error as UserError;
      throw apiError;
    }
  },
};
